import { Button } from "@aurumiq/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@aurumiq/ui/components/dropdown-menu";
import { Toaster } from "@aurumiq/ui/components/sonner";
import { useTheme } from "@aurumiq/ui/hooks/use-theme";
import { cn } from "@aurumiq/ui/lib/utils";
import { LogOut, Menu, Moon, Search, Settings2, Sun, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router";
import { useAuth } from "@/auth/auth-context";
import { hasPermission, useMe } from "@/auth/use-permission";

import { NAV } from "./nav";

export function Shell() {
  const auth = useAuth();
  const me = useMe();
  const { resolved, toggle } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-h-screen">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-60 flex-col bg-sidebar text-sidebar-foreground transition-transform lg:static lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        aria-label="Primary"
      >
        <div className="flex h-14 items-center gap-2 px-4 font-bold text-white">
          Aurum<b className="text-gold">IQ</b>
          <span className="ml-auto rounded-full bg-gold px-2 py-0.5 font-bold text-[11px] text-navy">
            ADMIN
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {NAV.map((group) => {
            const items = group.items.filter(
              (i) => !i.perm || hasPermission(me.data?.permissions, i.perm),
            );
            if (!items.length) return null;
            return (
              <div key={group.label} className="mt-4">
                <div className="px-2 pb-1 font-semibold text-[11px] text-sidebar-foreground/60 uppercase tracking-wider">
                  {group.label}
                </div>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] hover:bg-sidebar-accent hover:text-white",
                        isActive && "bg-sidebar-accent text-white",
                      )
                    }
                  >
                    <item.icon className="size-4" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="border-sidebar-border border-t px-4 py-3 text-[11px] text-sidebar-foreground/70">
          {me.data?.stub ? "auth/me stubbed" : (me.data?.role ?? "…")} · {auth.mode} auth
        </div>
      </aside>
      {open ? (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center gap-2 border-b bg-card px-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>
          <Button
            variant="outline"
            className="hidden w-72 justify-start text-muted-foreground sm:flex"
            onClick={() => window.dispatchEvent(new CustomEvent("aurumiq:command-palette"))}
          >
            <Search />
            Search… <kbd className="ml-auto rounded border px-1 text-[10px]">⌘K</kbd>
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
            {resolved === "dark" ? <Sun /> : <Moon />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Account">
                <UserRound />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <div className="font-semibold">{auth.user?.name}</div>
                <div className="font-normal text-muted-foreground text-xs">{auth.user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/account">
                  <Settings2 /> Account
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => auth.signOut()}>
                <LogOut /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-5 pb-16">
          <Outlet />
        </main>
      </div>
      <CommandPalettePlaceholder />
      <Toaster theme={resolved} />
    </div>
  );
}

/** M0 placeholder: opens on ⌘K / Ctrl+K; the real palette (cmdk) lands with the CMS editor. */
function CommandPalettePlaceholder() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("aurumiq:command-palette", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("aurumiq:command-palette", onOpen);
    };
  }, []);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-start pt-24">
      <button
        type="button"
        aria-label="Close command palette"
        className="absolute inset-0 bg-black/40"
        onClick={() => setOpen(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative mx-auto w-full max-w-lg rounded-xl border bg-card p-4 shadow-lg"
      >
        <div className="font-semibold">Command palette</div>
        <p className="mt-1 text-muted-foreground text-sm">
          Placeholder. Type-ahead navigation and actions arrive with M1.
        </p>
      </div>
    </div>
  );
}
